import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Cloud, Power, Zap, Lightbulb, Home, ShieldCheck } from 'lucide-react';
const CAPABILITIES = [{
  id: 'hassle-free',
  title: 'Hassle free',
  desc: 'No Docker, no servers, no SSH, no config files. Giga runs in the cloud with nothing to install, configure, or maintain.',
  icon: Cloud
}, {
  id: 'always-on',
  title: 'Always on',
  desc: 'Giga works around the clock handling tasks on your behalf: scheduled jobs, event-based triggers, and recurring actions.',
  icon: Power
}, {
  id: 'takes-action',
  title: 'Takes real action',
  desc: "This isn't a chatbot that gives suggestions. Giga sends messages, follows up with leads, pulls reports, and reacts to events on its own.",
  icon: Zap
}, {
  id: 'memory',
  title: 'Remembers and evolves',
  desc: 'Every conversation, every preference, every goal, carried across sessions. Giga picks up where it left off and refines its own rules.',
  icon: Lightbulb
}, {
  id: 'everywhere',
  title: 'Lives where you are',
  desc: 'Slack, Email, iMessage, and WhatsApp. No new interface to learn. Giga meets you wherever you already work.',
  icon: Home
}, {
  id: 'secure',
  title: 'Secure by default',
  desc: 'Permissions-based and sandboxed. Giga only accesses what you explicitly allow. No local servers. No open ports.',
  icon: ShieldCheck
}];
export function CapabilityGrid() {
  return <section className="w-full bg-[#FAF8F4] py-12 md:py-32" data-id="element-287">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-288">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16 md:mb-24" data-id="element-289">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 md:mb-6" data-id="element-290">
            Under the hood
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-5 md:mb-6 md:whitespace-nowrap" data-id="element-291">
            Built to do the work–{' '}
            <span className="text-[#EA580C]" data-id="element-292">not just talk about it.</span>
          </h2>
          <p className="text-base md:text-lg text-neutral-500 leading-relaxed max-w-2xl mx-auto" data-id="element-293">
            Everything beneath the surface that lets Giga take real action —
            around the clock, wherever you work.
          </p>
        </div>

        {/* Grid — internal dividers fade at the ends */}
        <div className="relative max-w-5xl mx-auto" data-id="element-294">
          {/* Fading internal dividers (desktop only) */}
          <div aria-hidden="true" className="hidden md:block pointer-events-none absolute inset-0 z-0" data-id="element-295">
            {/* Vertical rules at the 1/3 and 2/3 column boundaries */}
            <div className="absolute top-0 bottom-0 left-1/3 w-px bg-[linear-gradient(to_bottom,transparent,rgb(229_229_229)_14%,rgb(229_229_229)_86%,transparent)]" data-id="element-296" />
            <div className="absolute top-0 bottom-0 left-2/3 w-px bg-[linear-gradient(to_bottom,transparent,rgb(229_229_229)_14%,rgb(229_229_229)_86%,transparent)]" data-id="element-297" />
            {/* Horizontal rule between the two rows */}
            <div className="absolute left-0 right-0 top-1/2 h-px bg-[linear-gradient(to_right,transparent,rgb(229_229_229)_8%,rgb(229_229_229)_92%,transparent)]" data-id="element-298" />
          </div>

          <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 divide-y divide-neutral-100 md:divide-y-0" data-id="element-299">
            {CAPABILITIES.map((cap, index) => {
            const Icon = cap.icon;
            return <motion.div key={cap.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="group flex flex-row md:flex-col items-start text-left gap-4 md:gap-0 py-5 md:p-12 md:hover:bg-[#2B211A] md:hover:shadow-xl md:hover:z-20 md:transition-all md:duration-300" data-id="element-300">
                  {/* Icon — compact tinted tile on mobile, large thin icon on desktop */}
                  <div className="shrink-0 flex items-center justify-center w-11 h-11 rounded-xl bg-[#EA580C]/[0.08] md:w-auto md:h-16 md:rounded-none md:bg-transparent md:mb-12 md:transition-colors md:duration-300" data-id="element-301">
                    <Icon strokeWidth={1.25} className="w-[22px] h-[22px] md:w-16 md:h-16 text-[#EA580C] md:group-hover:text-white md:transition-colors md:duration-300" data-id="element-302" />
                  </div>

                  {/* Text Content */}
                  <div className="flex-1 min-w-0" data-id="element-303">
                    <h3 className="text-base md:text-xl font-semibold mb-1 md:mb-4 text-[#2B211A] md:group-hover:text-white md:transition-colors md:duration-300" data-id="element-304">
                      {cap.title}
                    </h3>
                    <p className="leading-relaxed text-sm md:text-[15px] text-neutral-500 md:group-hover:text-white/80 md:transition-colors md:duration-300" data-id="element-305">
                      {cap.desc}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}